/**
 * standingToLibrary - the end-of-session save, into the GM's library too (2026-11-04).
 *
 * The characters export's "Save to library as they stand" wrote only the
 * session's own list; now each person also lands in the asset library, where
 * `engine/assets/saveBack` says: the next version of the asset they came from,
 * a new asset beside it for an extra body of one sheet, or "From session
 * <code>" for anyone from nowhere. The session's copy is then pointed at what
 * was saved, so the next save versions the same asset and the copy never
 * reads as "newer in the library".
 *
 * One at a time, and each save seen by the next: the library this batch
 * was handed is a render behind, so the batch carries what it saved (and
 * the session folder it made) forward itself - otherwise two people from
 * nowhere make two "From session" folders, two new assets in one folder
 * take one name, and the room check never counts the batch. A failure is
 * that person's sentence, never the batch's: the rest still save.
 *
 * Each person is written to the session's list ONCE, after their library
 * save, pointed at what it made; a person the library refused keeps the
 * pointer their copy had, so the next save still finds their asset.
 */
import { afterSave, newBesideAtTable, saveMadeAtTable, type LibraryAccess } from '../assetSession';
import { KINDS } from '../engine/assets/kinds';
import { saveBackPlan, type SaveBack, type SaveBackBody } from '../engine/assets/saveBack';
import { pathOf } from '../engine/assets/tree';
import type { AssetRow, Origin } from '../engine/assets/types';
import type { Character } from '../engine/character/types';
import { messageOf, pushSheet, sessionSheets, type CopyShell } from './libraryCopyIn';

export interface StandingShell extends CopyShell {
  session?: { code?: string | null } | null;
}

/** What one batch did: each saved person as "Diocles v8 (Misiones/Misión 6/Personajes)", each failure as a sentence. */
export interface StandingSaved {
  saved: string[];
  failed: string[];
}

/** The asset `place` names, saved: the next version of one, a new one beside one, or a new one in the session's. */
function saveAt(access: LibraryAccess, { entry, place }: SaveBack, label: string): Promise<AssetRow> {
  const { origin: _copy, ...plain } = entry;
  const made = { kind: 'character' as const, name: entry.name, text: KINDS.character.encode(plain) };
  if (place.kind === 'beside') return newBesideAtTable(access, made, place.of, label);
  // With no origin, saveMadeAtTable makes the new asset in "From session <label>".
  return saveMadeAtTable(access, { ...made, origin: place.kind === 'version' ? place.origin : null }, label);
}

/** Where a saved asset sits, as the library names it: "Misiones/Misión 6/Personajes", or My assets at the top. */
function whereIs(access: LibraryAccess, saved: AssetRow): string {
  const path = pathOf(access.lib.folders, saved.folderId).map((f) => f.name);
  return path.length ? path.join('/') : 'My assets';
}

/** The person as the session's list keeps them: pointed at `origin`, or at nothing when there is none. */
function pointedAt(entry: Character, origin: Origin | undefined): Character {
  const { origin: _was, ...plain } = entry;
  return origin ? { ...plain, origin } : plain;
}

/** Save each body to the library where its plan says, then to the session's list, pointed at the version made. */
export async function versionStanding(
  s: StandingShell,
  handed: LibraryAccess,
  bodies: readonly SaveBackBody[],
): Promise<StandingSaved> {
  const label = s.session?.code ?? 'at the table';
  const sheets = sessionSheets(s);
  const done: StandingSaved = { saved: [], failed: [] };
  let access = handed;
  for (const step of saveBackPlan(bodies, sheets)) {
    let origin = sheets.find((c) => c.id === step.entry.id)?.origin;
    try {
      const saved = await saveAt(access, step, label);
      access = afterSave(access, saved, label);
      origin = { assetId: saved.id, version: saved.version };
      done.saved.push(`${saved.name} v${saved.version} (${whereIs(access, saved)})`);
    } catch (err) {
      done.failed.push(`${step.entry.name}: ${messageOf(err)}`);
    }
    pushSheet(s, pointedAt(step.entry, origin));
  }
  return done;
}
